import { DESTINATIONS, type DestinationId } from '@/components/visitone/navigation-model'
import type { Access } from '@/lib/bff/access'
import { SETUP_LANDLORDS_PATH } from '@/lib/routing'

/*
 * Pure helpers over the caller's access decision in the current tenant
 * (VIS-329). Every helper fails closed: with no decision (`null`), nothing
 * can be opened, changed or offered.
 */

export function canView(access: Access | null, destination: DestinationId): boolean {
  return Boolean(access?.destinations.some((entry) => entry.id === destination))
}

export function canChange(access: Access | null, destination: DestinationId): boolean {
  return Boolean(access?.destinations.some((entry) => entry.id === destination && entry.mode === 'change'))
}

export function can(access: Access | null, action: string): boolean {
  return Boolean(access?.actions.includes(action))
}

/** Whether a plan-limited feature is held (roles permit and plan includes). Fails closed on `null`. */
export function hasFeature(access: Access | null, feature: string): boolean {
  return Boolean(access?.features.some((entry) => entry.id === feature))
}

/** Permitted destinations in rail order. */
export function permittedDestinations(access: Access | null): DestinationId[] {
  return DESTINATIONS.filter((destination) => canView(access, destination.id)).map((destination) => destination.id)
}

/** Where to land: Today if permitted, otherwise the first permitted destination in rail order, otherwise null. */
export function landing(access: Access | null): DestinationId | null {
  if (canView(access, 'today')) return 'today'
  return permittedDestinations(access)[0] ?? null
}

/**
 * Destinations listed in the rail. `landlords.manage` is a principal action,
 * not a tenant destination, but Landlords lives under Setup, so Setup is
 * listed when the action is held even if the decision has no setup
 * destination. Favourites and the rest of Setup still follow that destination.
 */
export function railDestinations(access: Access | null): DestinationId[] {
  const permitted = permittedDestinations(access)
  if (!can(access, 'landlords.manage') || permitted.includes('setup')) return permitted
  return DESTINATIONS.filter((destination) => permitted.includes(destination.id) || destination.id === 'setup').map(
    (destination) => destination.id,
  )
}

/** Whether Setup's index opens: with the setup destination, or with `landlords.manage`, its way into Landlords. */
export function canOpenSetupHome(access: Access | null): boolean {
  return canView(access, 'setup') || can(access, 'landlords.manage')
}

export type HomeTarget = { path: string; labelKey: string }

/**
 * Where `/` lands, and where a refused page sends the caller: Today if
 * permitted, otherwise the first permitted destination, otherwise Landlords
 * when that principal action is held.
 */
export function homeTarget(access: Access | null): HomeTarget | null {
  const destination = landing(access)
  if (destination) {
    const entry = DESTINATIONS.find((item) => item.id === destination)
    if (entry) return { path: entry.route, labelKey: entry.labelKey }
  }
  if (can(access, 'landlords.manage')) return { path: SETUP_LANDLORDS_PATH, labelKey: 'landlords.title' }
  return null
}

export type AccessPill = 'viewOnly'

export type PageStatus = {
  /** False when the page must not be shown at all; a direct attempt is `RefusedSurface`. */
  permitted: boolean
  /** Change controls are removed (absent, never disabled). */
  readOnly: boolean
  /** Pills for `PageHeader.status`, in reading order. */
  pills: AccessPill[]
  /** `usual`: the page's own summary; `viewOnly`: the View only line instead; `none`: no summary. */
  summary: 'usual' | 'viewOnly' | 'none'
  body: 'normal' | 'refused'
}

/*
 * One precedence rule for every page header (VIS-329). `feature` is the page's
 * plan-limited feature, if it has one. When the feature is not held, the page
 * is refused like a missing capability (VIS-527).
 */
export function pageStatus(access: Access | null, destination: DestinationId, feature?: string): PageStatus {
  const refused: PageStatus = { permitted: false, readOnly: true, pills: [], summary: 'none', body: 'refused' }
  if (!canView(access, destination)) return refused

  if (feature !== undefined && !hasFeature(access, feature)) return refused

  const viewOnly = !canChange(access, destination)
  const pills: AccessPill[] = viewOnly ? ['viewOnly'] : []
  return { permitted: true, readOnly: viewOnly, pills, summary: viewOnly ? 'viewOnly' : 'usual', body: 'normal' }
}

/*
 * Plan-limited features the interface can name and open, by `getAccess`
 * feature id. Each feature's own story adds its entry (AC 10); V1 has none.
 * An entry is offered only when `getAccess.features` holds it (VIS-527).
 */
export const PLAN_LIMITED_FEATURES: Record<string, { labelKey: string; destination: DestinationId; route: string }> = {}
